import { useCallback, useContext, useEffect, useRef, useState } from "react";

import SearchIcon from "~/icons/SearchIcon";
import ThemeContext from "~/contexts/ThemeContext";
import GoogleContext from "~/contexts/GoogleContext";

import styles from "./AddressPicker.module.css";
import LocationIcon from "~/icons/LocationIcon";
import { PlaceDetails } from "~/helpers/coordsHelpers";

// Small helper type for predictions
type PlacePrediction = google.maps.places.PlacePrediction;

type AddressPickerProps = {
  placeholder?: string;
  autofill?: boolean;
  address: string;
  setAddress: (value: string) => void;
  onPlaceSelect?: (details: PlaceDetails) => void;
  storeLocation?: google.maps.LatLngLiteral;
  radiusMeters?: number;
};

const AddressPicker: React.FC<AddressPickerProps> = ({
  placeholder = "Enter your address",
  autofill = false,
  address,
  setAddress,
  onPlaceSelect,
  storeLocation,
  radiusMeters = 10000,
}) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const { template } = useContext(ThemeContext);
  const { isLoaded } = useContext(GoogleContext);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const initSessionToken = useCallback(async () => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    const placesLib = (await window.google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  useEffect(() => {
    initSessionToken();
  }, [initSessionToken]);

  // Autofill current location
  useEffect(() => {
    if (!autofill || !isLoaded || !window.google?.maps?.Geocoder) {
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results, status) => {
          if (status === "OK" && results?.[0]?.formatted_address) {
            setAddress(results[0].formatted_address);
            setPredictions([]);
          } else {
            console.error("Failed to get address by coordinates");
          }
        },
      );
    });
  }, [address, autofill, isLoaded, setAddress]);

  const fetchPredictions = useCallback(async () => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    // If empty input, close dropdown immediately
    if (!address.trim()) {
      setPredictions([]);
      return;
    }
    const placesLib = (await window.google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    // Ensure token exists
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }

    const bounds = new window.google.maps.Circle({
      center: storeLocation,
      radius: radiusMeters,
    }).getBounds();
    const boundsLiteral = bounds?.toJSON();

    const autocompleteRequest: google.maps.places.AutocompleteRequest = {
      input: address,
      sessionToken: sessionTokenRef.current,
      origin: storeLocation,
    };

    if (boundsLiteral) {
      autocompleteRequest.locationRestriction = boundsLiteral;
    }

    // Fetch autocomplete suggestions
    const { suggestions } =
      await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        autocompleteRequest,
      );

    // Take only place predictions
    const placePredictions = suggestions
      .map((s) => s.placePrediction)
      .filter((p): p is PlacePrediction => Boolean(p));

    setPredictions(placePredictions);
  }, [address, isLoaded, radiusMeters, storeLocation]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  // Selecting a prediction -> fetch place details via Place.fetchFields()
  const handleSelect = async (p: PlacePrediction) => {
    if (!isLoaded || !window.google?.maps?.importLibrary) return;

    const placesLib = (await window.google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    const place = p.toPlace();

    await place.fetchFields({
      fields: ["formattedAddress", "location", "addressComponents"],
    });

    if (place.formattedAddress) {
      setAddress(place.formattedAddress);
    }

    if (onPlaceSelect && place.formattedAddress && place.location) {
      onPlaceSelect({
        formattedAddress: place.formattedAddress,
        latitude: place.location.lat(),
        longitude: place.location.lng(),
        addressComponents: place.addressComponents ?? [],
      });
    }

    sessionTokenRef.current = new placesLib.AutocompleteSessionToken();

    inputRef.current?.blur();
  };

  // Styles
  const inputStyles = {
    backgroundColor: template.global.input.backgroundColour,
    borderColor: template.global.input.borderColour,
    color: template.global.input.fontColour,
    "--focus-search-select-background-colour":
      template.global.input.focusBackgroundColour,
    "--focus-search-select-border-colour":
      template.global.input.focusBorderColour,
  };

  return (
    isLoaded && (
      <div className={styles.wrapper}>
        <div className={styles.searchIconWrapper}>
          <SearchIcon />
        </div>

        <input
          type="text"
          placeholder={placeholder}
          ref={inputRef}
          className={styles.input}
          style={inputStyles as React.CSSProperties}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          autoComplete="off"
        />

        {isFocused && predictions.length > 0 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            {predictions.map((p) => (
              <button
                key={p.placeId}
                type="button"
                className={styles.dropdownItem}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(p)}
              >
                <LocationIcon />
                {p.text?.text ?? ""}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  );
};

export default AddressPicker;
